import {ErrorLocationType, HttpType} from "@/utilities/system/enums/Enums";
import {WebCallService} from "@/utilities/system/services/WebCallService";
import {PathHelper} from "@/utilities/system/helpers/PathHelper";
import {
    AddedUserResponse,
    MinifiedWorkersResponse,
    ProviderWorkerSearchResponse,
    UserResponse,
    WorkersResponse
} from "@/models/api/responses";
import {ProviderWorker} from "@/models/entities/providerWorkers/ProviderWorker";
import NullOrUndefinedHelper from "@/utilities/system/helpers/NullOrUndefinedHelper";
import {PhoneNumberRequest, WorkerRequest} from "@/models/api/requests";
import {GeneralResponse} from "@/models/system/api/Responses";
import {Provider} from "@/models/entities/providers/Provider";
import ErrorHandler from "@/utilities/system/helpers/ErrorHandler";
import { ResponseValidator } from "@/utilities/system/helpers/ResponseValidator";

export class ProviderWorkerService {
    private webCaller: WebCallService;
    private uriHelper: PathHelper;
    private responseValidator: ResponseValidator;

    constructor(){
        this.webCaller = new WebCallService();
        this.uriHelper = new PathHelper();
        this.responseValidator = new ResponseValidator();
    }

    public async getProviderWorkersForProvider(providerId:string, errorLocationType:ErrorLocationType): Promise<WorkersResponse|null> {
        const uri = this.uriHelper.workers.getProviderWorkersPath(providerId);

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async getProviderWorkersMinified(errorLocationType:ErrorLocationType): Promise<MinifiedWorkersResponse|null> {
        const uri = this.uriHelper.workers.getProviderWorkersSelectablePath();

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async getProviderWorkerById(id:string, errorLocationType:ErrorLocationType): Promise<UserResponse|null> {
        const uri = this.uriHelper.workers.getProviderWorkerByIdPath(id);

        const resp = await this.webCaller.HttpSend(HttpType.get, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async addOrUpdateProviderWorker(providerId:string, worker:ProviderWorker, errorLocationType:ErrorLocationType): Promise<AddedUserResponse|null> {
        const uri = NullOrUndefinedHelper(worker.id)
            ? this.uriHelper.workers.getProviderWorkerByIdPath(providerId)
            : this.uriHelper.workers.getProviderWorkerByProviderAndUserPath(providerId, worker.id);

        const requestBody = new WorkerRequest(worker);

        const reqType = NullOrUndefinedHelper(worker.id)
            ? HttpType.post
            : HttpType.put;

        const resp = await this.webCaller.HttpSend(reqType, uri, requestBody);

        // user belongs to a different provider so show error with company name
        if (resp && resp.statusCode === 4028) {
            resp.statusDescription = `${resp.statusDescription} (${resp.companyName})`;
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async deleteProviderWorker(providerId:string, workerId:string, errorLocationType:ErrorLocationType): Promise<GeneralResponse|null> {
        const uri = this.uriHelper.workers.getProviderWorkerByProviderAndUserPath(providerId, workerId);

        const resp = await this.webCaller.HttpSend(HttpType.delete, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async postResetExpirationForProviderWorker(providerId: string, userId: string, errorLocationType:ErrorLocationType): Promise<GeneralResponse|null> {
        const uri = this.uriHelper.workers.getResetExpirationForProviderWorkerPath(providerId, userId);
        const requestBody = false;

        const resp = await this.webCaller.HttpSend(HttpType.post, uri, requestBody);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async postSetProviderWorkerEnabled(id: string, status: boolean, errorLocationType:ErrorLocationType): Promise<GeneralResponse|null> {
        const uri = this.uriHelper.workers.getProviderWorkerStatusPath(id);
        const requestBody = status;

        const resp = await this.webCaller.HttpSend(HttpType.post, uri, requestBody);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async postSearchProviderWorkerByPhonenumber(providerId: string, dialCode: string, phoneNumber: string, errorLocationType:ErrorLocationType): Promise<ProviderWorkerSearchResponse|null> {
        const uri = this.uriHelper.workers.getSearchProviderWorkerByPhonenumberPath(providerId);
        const requestBody = new PhoneNumberRequest(dialCode, phoneNumber);

        const resp = await this.webCaller.HttpSend(HttpType.post, uri, requestBody);

        // if user doesnt exist it is still valid to try and add him so return a non-response
        if (resp && resp.statusCode === 4000) return new ProviderWorkerSearchResponse();

        // user belongs to a different provider so show error with company name
        if (resp && resp.statusCode === 4028) {
            resp.statusDescription = `${resp.statusDescription} (${resp.companyName})`;
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }

    public async postPromoteToProviderAdmin(provider: Provider, user: ProviderWorker, errorLocationType:ErrorLocationType): Promise<GeneralResponse|null> {
        const uri = this.uriHelper.workers.getPromoteProviderWorkerToAdminPath(provider.id, user.id ?? user.userId);

        const resp = await this.webCaller.HttpSend(HttpType.put, uri);
        if (!this.responseValidator.ValidateResponse(resp)) {
            ErrorHandler.handleError(resp, errorLocationType);
            return null;
        }

        return resp;
    }
}